'use client';

import { ChangeEvent, useMemo } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

import { TableDropdown } from './TableDropdown';
import {
  ISignUpsListingData,
  TableBodyCell,
  TableHeaderCell,
} from './TableCell';

const itemsPerPageData = [
  { label: 5, value: 5 },
  { label: 10, value: 10 },
  { label: 15, value: 15 },
  { label: 25, value: 25 },
];

/**
 * Header configuration for the table.
 */
export interface IHeader {
  /** Unique key identifying the column. */
  key: string;
  /** Display label for the column header. */
  label: string;
}

/**
 * Supports nested header structures for complex table layouts.
 */
export interface INestedHeader {
  [index: number]: IHeader | IHeader[];
}

/**
 * Properties for the Table component.
 */
interface ITableProps<T> {
  /** The current active page number. */
  currentPage: number;
  /** The total count of items across all pages. */
  count: string;
  /** The data to be displayed in the current table view. */
  data: T[];
  /** Function to fetch the previous page of data. */
  fetchPreviousPage: () => void;
  /** Function to fetch the next page of data. */
  fetchNextPage: () => void;
  /** Whether there is a previous page available. */
  hasPreviousPage: boolean;
  /** Whether there is a next page available. */
  hasNextPage: boolean;
  /** The configuration for table column headers. */
  headers: INestedHeader[] | IHeader[];
  /** The number of items to display per page. */
  itemsPerPage: number;
  /** Function to set the current active page. */
  setCurrentPage: (page: number) => void;
  /** Function to set the number of items per page. */
  setItemsPerPage: (itemsPerPage: number) => void;
}

/**
 * -----------------------------------------------------------------------------
 * It renders a dynamic table with with customization of cell.
 * @deprecated
 */
export function TableV1<T extends ISignUpsListingData>({
  currentPage,
  count,
  data,
  fetchPreviousPage,
  fetchNextPage,
  hasPreviousPage,
  hasNextPage,
  headers,
  itemsPerPage,
  setCurrentPage,
  setItemsPerPage,
}: Readonly<ITableProps<T>>) {
  const itemCount = parseInt(count, 10);
  const totalPage = Math.ceil(itemCount / itemsPerPage);

  const currentPageData = useMemo(
    () =>
      Array.from({ length: totalPage }, (_, index) => ({
        label: index + 1,
        value: index + 1,
      })),
    [totalPage]
  );
  const lastItemNumberForPage =
    currentPage * itemsPerPage > itemCount + 1
      ? itemCount
      : currentPage * itemsPerPage;

  const firstItemNumberForPage =
    lastItemNumberForPage < (currentPage - 1) * itemsPerPage + 1
      ? itemCount
      : (currentPage - 1) * itemsPerPage + 1;

  const handleItemsPerPage = (e: ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(parseInt(e.target.value, 10));
  };

  const handleCurrentPage = (e: ChangeEvent<HTMLSelectElement>) => {
    setCurrentPage(parseInt(e.target.value, 10));
  };

  const handleNextPage = () => {
    fetchNextPage();
    setCurrentPage(currentPage + 1);
  };

  const handlePreviousPage = () => {
    fetchPreviousPage();
    setCurrentPage(currentPage - 1);
  };

  return (
    <div>
      <div className="bg-white">
        <table
          className="w-full text-start text-gray-950 dark:text-gray-300
          "
        >
          <thead
            className="
              z-10 whitespace-nowrap font-medium text-gray-950
              dark:bg-gray-900 dark:text-white
            "
          >
            <tr className="px-2 py-2.5 md:px-4">
              {headers.map((value) => {
                const { key, label } = value as IHeader;
                return (
                  <th
                    key={key}
                    className="'px-2 bg-gray-50 py-3 text-xs font-medium md:px-4"
                  >
                    <div className="flex flex-col">
                      {Array.isArray(value) ? (
                        value.map(({ key: nestedKey, label: nestedLabel }) => (
                          <span key={nestedKey} className="last:text-gray-400">
                            <TableHeaderCell
                              headerKey={nestedKey}
                              label={nestedLabel}
                            />
                          </span>
                        ))
                      ) : (
                        <TableHeaderCell headerKey={key} label={label} />
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {data?.map((item) => (
              <tr key={item.id} className="px-2 py-2.5 md:px-4">
                {headers.map((value) => {
                  const { key } = value as IHeader;
                  return (
                    <td
                      key={key}
                      className="px-2 py-2.5 first:w-12 first:pe-0 md:px-4"
                    >
                      <div className="flex w-fit flex-col text-xs">
                        {Array.isArray(value) ? (
                          value.map(({ key: nestedKey }) => (
                            <span
                              key={nestedKey}
                              className="last:text-gray-400"
                            >
                              <TableBodyCell
                                headerKey={nestedKey}
                                item={item}
                              />
                            </span>
                          ))
                        ) : (
                          <TableBodyCell
                            headerKey={key as keyof ISignUpsListingData}
                            item={item}
                          />
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex w-full justify-between px-5 py-2 text-xs">
        <div
          className="
            flex h-fit items-center gap-2 rounded-lg border border-gray-200
            bg-green-100 px-2 py-1.5
          "
        >
          <TableDropdown
            data={itemsPerPageData}
            onChange={handleItemsPerPage}
            selectedValue={itemsPerPage}
          />
          <div className="whitespace-nowrap text-gray-400">Items per page</div>
        </div>
        <div className="flex items-center gap-2 text-gray-400">
          <span className="whitespace-nowrap">
            {`${firstItemNumberForPage}-${lastItemNumberForPage} of ${itemCount} items`}
          </span>
          <div className="rounded-lg bg-green-100">
            <TableDropdown
              data={currentPageData}
              onChange={handleCurrentPage}
              selectedValue={currentPage}
            />
          </div>
          <span className="whitespace-nowrap">{`of ${totalPage} pages`}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="text-gray-800 disabled:text-gray-300"
            disabled={!hasPreviousPage}
            onClick={handlePreviousPage}
            type="button"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>
          <button
            className="text-gray-800 disabled:text-gray-300"
            disabled={!hasNextPage}
            onClick={handleNextPage}
            type="button"
          >
            <ChevronRightIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
